import { Visibility, VisibilityOff } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Divider, Grid2, IconButton, InputAdornment, Paper, TextField, Toolbar, Typography } from "@mui/material";
import { Formik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import * as Yup from "yup";
import { useFormStyles } from "../../hooks/useFormStyles";
import { UpdateUserRequest, User } from "../../types/interfaces/user";
import { UpdateUserPasswordRequest } from "../../types/model/user";

interface UserProfileFormFormProps {
  userData: User;
  onUpdate: (values: UpdateUserRequest) => Promise<boolean>;
  onUpdatePassword: (values: UpdateUserPasswordRequest) => void;
}

export default function UserProfileForm({
  userData,
  onUpdate,
  onUpdatePassword,
}: Readonly<UserProfileFormFormProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [updatePassword, setUpdatePassword] = useState(false);

  const formStyles = useFormStyles();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const userValidationSchema = Yup.object().shape({
    name: Yup.string().required(t("admin.users.user-name-required")),
    email: Yup.string()
      .email(t("admin.users.user-email-invalid"))
      .required(t("admin.users.user-email-required")),
    phoneNumber: Yup.string(),
  });

  const passwordValidationSchema = Yup.object().shape({
    password: Yup.string()
      .min(6, t("admin.users.user-password-min"))
      .required(t("admin.users.user-password-required")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], t("admin.users.user-password-match"))
      .required(t("admin.users.user-password-confirmation-required")),
  });

  const handleSubmitUser = async (
    values: { name: string; email: string; phoneNumber: string },
  ) => {
    if (!userData) return;

    const requestBody: UpdateUserRequest = {
      ...values,
      roleId: userData.roleId,
      enabled: userData.enabled,
    };

    const success = await onUpdate(requestBody);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleSubmitPassword = (
    values: { password: string; confirmPassword: string },
    resetForm: () => void,
  ) => {
    onUpdatePassword(values);
    resetForm();
    setShowPassword(false);
    setShowConfirm(false);
    setUpdatePassword(false);
  };

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Formik
        initialValues={{
          name: userData.name || "",
          email: userData.email || "",
          phoneNumber: userData.phoneNumber || "",
        }}
        validationSchema={userValidationSchema}
        enableReinitialize
        onSubmit={handleSubmitUser}
      >
        {({ values, touched, errors, handleChange, handleBlur, handleSubmit, resetForm }) => (
          <Box component="form" id="user-profile-form" onSubmit={handleSubmit}>
            <Toolbar sx={{ mb: 3 }}>
              <Typography sx={{ flex: "1 1 100%" }} variant="h5">
                {t("admin.users.my-profile-header")}
              </Typography>

              <Button
                type="button"
                variant="contained"
                startIcon={<ArrowBackIcon />}
                sx={{ textTransform: "none", borderRadius: 2 }}
                onClick={() => navigate(-1)}
              >
                {t("generic.back")}
              </Button>
            </Toolbar>

            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <TextField
                  fullWidth
                  name="name"
                  label={`${t("admin.users.name")} *`}
                  variant={isEditing ? "outlined" : "standard"}
                  disabled={!isEditing}
                  sx={{ ...formStyles }}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!(errors.name && touched.name)}
                  helperText={touched.name ? errors.name : ""}
                />
              </Grid2>

              <Grid2 size={6}>
                <TextField
                  fullWidth
                  name="email"
                  label={`${t("admin.users.email")} *`}
                  variant={isEditing ? "outlined" : "standard"}
                  disabled={!isEditing}
                  sx={{ ...formStyles }}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!(errors.email && touched.email)}
                  helperText={touched.email ? errors.email : ""}
                />
              </Grid2>

              <Grid2 size={6}>
                <TextField
                  fullWidth
                  name="phoneNumber"
                  label={t("admin.users.phone-number")}
                  variant={isEditing ? "outlined" : "standard"}
                  disabled={!isEditing}
                  sx={{ ...formStyles }}
                  value={values.phoneNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!(errors.phoneNumber && touched.phoneNumber)}
                  helperText={touched.phoneNumber ? errors.phoneNumber : ""}
                />
              </Grid2>
            </Grid2>

            {/* Action Buttons */}
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                p: 2,
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 1,
              }}
            >
              {!isEditing && !updatePassword && (
                <>
                  <Button variant="outlined" type="button" onClick={() => setIsEditing(true)}>
                    {t("generic.edit")}
                  </Button>
                  <Button variant="outlined" type="button" onClick={() => setUpdatePassword(true)}>
                    {t("admin.users.user-update-password")}
                  </Button>
                </>
              )}

              {isEditing && (
                <>
                  <Button
                    variant="outlined"
                    color="secondary"
                    type="button"
                    onClick={() => {
                      resetForm();
                      setIsEditing(false);
                    }}
                  >
                    {t("generic.cancel")}
                  </Button>
                  <Button type="submit" form="user-profile-form" variant="outlined">
                    {t("generic.save")}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        )}
      </Formik>

      {/* Update Password Form */}
      {updatePassword && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1 }}>
              {t("admin.users.user-update-password")}
            </Typography>
          </Toolbar>

          <Divider />

          <Box sx={{ p: 3 }}>
            <Formik
              initialValues={{ password: "", confirmPassword: "" }}
              validationSchema={passwordValidationSchema}
              onSubmit={(values, { resetForm }) => handleSubmitPassword(values, resetForm)}
            >
              {({ values, touched, errors, handleChange, handleBlur, handleSubmit, resetForm }) => (
                <form id="update-password-form" onSubmit={handleSubmit}>
                  <Grid2 container spacing={3}>
                    {/* Password */}
                    <Grid2 size={6}>
                      <TextField
                        fullWidth
                        type={showPassword ? "text" : "password"}
                        name="password"
                        label={`${t("admin.users.password")} *`}
                        variant="outlined"
                        sx={{ ...formStyles }}
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!(errors.password && touched.password)}
                        helperText={touched.password ? errors.password : ""}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword((prev) => !prev)}
                                  edge="end"
                                  aria-label={
                                    showPassword
                                      ? t("admin.users.hide-password")
                                      : t("admin.users.show-password")
                                  }
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Grid2>

                    {/* Confirm Password */}
                    <Grid2 size={6}>
                      <TextField
                        fullWidth
                        type={showConfirm ? "text" : "password"}
                        name="confirmPassword"
                        label={`${t("admin.users.password-confirm")} *`}
                        variant="outlined"
                        sx={{ ...formStyles }}
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!(errors.confirmPassword && touched.confirmPassword)}
                        helperText={touched.confirmPassword ? errors.confirmPassword : ""}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowConfirm((prev) => !prev)}
                                  edge="end"
                                  aria-label={
                                    showConfirm
                                      ? t("admin.users.hide-password")
                                      : t("admin.users.show-password")
                                  }
                                >
                                  {showConfirm ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Grid2>
                  </Grid2>

                  <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 3 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      type="button"
                      onClick={() => {
                        resetForm();
                        setShowPassword(false);
                        setShowConfirm(false);
                        setUpdatePassword(false);
                      }}
                    >
                      {t("generic.cancel")}
                    </Button>

                    <Button type="submit" form="update-password-form" variant="contained">
                      {t("admin.users.user-update-password")}
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          </Box>
        </Paper>
      )}
    </Paper>
  );
}